import { GoogleOAuthProvider } from '@react-oauth/google';
import { gapi } from 'gapi-script';
import React, { useEffect } from 'react';
import { useGoogleClientId } from 'app/hooks/useGoogleClientId';

/**
 * GoogleAuthProvider
 *
 * Mounts GoogleOAuthProvider with the clientId resolved from `organization`
 * (with fallback to the per-origin map and the generic OneClick client id).
 * It must stay mounted below TranslationsProvider, which blocks rendering
 * until the organization is loaded — so the first render already sees the
 * API-provided clientId (gapi.auth2.init only honours the first call).
 * It also loads gapi's client/auth modules (used by the Google Drive Picker
 * and the Classroom import) and initializes gapi.auth2 with the same clientId.
 */

type GoogleAuthProviderProps = {
  children: React.ReactNode;
};

function GoogleAuthProvider({ children }: Readonly<GoogleAuthProviderProps>): JSX.Element {
  const clientId = useGoogleClientId();

  useEffect(() => {
    // gapi.load must run unconditionally: googleClassroom.js relies on
    // window.gapi.auth/client being loaded even when there is no clientId
    // (otherwise opening the Classroom import dialog throws a TypeError).
    gapi.load('client:auth2', () => {
      if (!clientId) return;
      gapi.auth2.init({ clientId, plugin_name: 'chat' });
    });
  }, [clientId]);

  return <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider>;
}

export default GoogleAuthProvider;
